import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center font-body font-medium justify-center gap-2 whitespace-nowrap rounded-full text-sm transition-all duration-150 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        // The primary action, in the glass language of the active tab and the count pill:
        // radial primary fill + gradient rim (`.btn-glass-primary`, index.css), heading type.
        default:
          "btn-glass-primary text-primary-foreground font-heading font-semibold hover:brightness-110 active:scale-[0.98]",
        optin:
          "bg-gradient-optin border border-primary text-primary-foreground hover:border-primary font-heading font-semibold shadow-lg shadow-primary/10",
        destructive:
          "bg-destructive text-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent text-foreground hover:text-accent-foreground dark:bg-input/30 dark:border-border dark:hover:bg-input/50",
        // The toned-down action — same glass as the tab track and the close button, so it sits
        // beside a primary without competing (close, "show all", dialog actions).
        secondary:
          "btn-glass text-foreground-secondary hover:text-foreground hover:brightness-110 active:scale-[0.98]",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        // The quiet decline under a primary ("Not now", "Later"): text only, no surface, so it
        // never reads as a second choice of equal weight — but still a full 44px tap target and a
        // visible focus ring (`.btn-quiet`, plain CSS: Tailwind's ring utilities don't render in
        // the widget's shadow root). Pair it with a `default` button above it.
        quiet:
          "btn-quiet min-h-[44px] bg-transparent px-4 font-body font-medium text-muted-foreground underline-offset-[3px] hover:text-foreground hover:underline focus-visible:text-foreground focus-visible:underline",
        link: "text-primary underline-offset-4 hover:underline",
      },
      // Side padding goes through `--btn-px`, and an icon only tightens the VARIABLE: a caller's
      // own `px-*` then replaces `px-(--btn-px)` outright (tailwind-merge), instead of losing to
      // a `has-[>svg]:px-*` rule whose `:has()` selector out-specifies any plain utility.
      size: {
        default:
          "h-10 px-(--btn-px) py-2 [--btn-px:24px] has-[>svg]:[--btn-px:12px]",
        sm: "h-8 gap-1.5 px-(--btn-px) [--btn-px:12px] has-[>svg]:[--btn-px:10px]",
        lg: "h-10 px-(--btn-px) [--btn-px:24px] has-[>svg]:[--btn-px:16px]",
        icon: "size-10 rounded-full ",
        "icon-sm": "size-8 rounded-full ",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  type = "button",
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      type={type}
      {...props}
    />
  );
}

export { Button, buttonVariants };
